import React, { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useRootStore } from "mobx-stores/store-provider";
import { ShopifyLoginPayload, User } from "services";
import { RE_CAPTCHA_SITE_KEY } from "config/environment";
import { Logo } from "components/shopify/logo/logo";
import validate from "validate.js";
import { history } from "mobx-stores";
import styles from "./shopify-login-page.module.scss";
import { ReactComponent as GoogleIcon } from "assets/icons/svg/google.svg";
import { ReactComponent as MicrosoftIcon } from "assets/icons/svg/microsoft.svg";
import { ReactComponent as AppleIcon } from "assets/icons/svg/apple.svg";

const shopifyLoginFormConstraints = {
  emailAddress: {
    presence: true,
    format: {
      pattern:
        /^[a-z0-9][a-z0-9-_.]+@([a-z]|[a-z0-9]?[a-z0-9-]+[a-z0-9])\.[a-z0-9]{2,10}(?:\.[a-z]{2,10})?$/,
    },
  },
  password: {
    presence: true,
    length: {
      minimum: 2,
    },
  },
  reCaptchaToken: {
    presence: true,
    length: {
      minimum: 2,
    },
  },
};

const socialRegisterFormConstraints = {
  companyName: {
    presence: { allowEmpty: false },
    length: { minimum: 2 },
  },
  shopifySiteUrl: {
    presence: { allowEmpty: false },
    format: {
      pattern: /^https?:\/\/.+\..+/,
      message: "must be a valid URL (e.g. https://mystore.myshopify.com)",
    },
  },
};

interface SocialRegisterForm {
  companyName: string;
  shopifySiteUrl: string;
}

interface SocialRegisterErrors {
  companyName?: string;
  shopifySiteUrl?: string;
}

const ShopifyLoginPage: React.FC = () => {
  const {
    generalStore: { api },
    userStore: {
      socialLogin,
      socialRegister,
      getGoogleAuthUrl,
      getMicrosoftAuthUrl,
      getAppleAuthUrl,
    },
  } = useRootStore();

  // --- Standard login form ---
  const [formState, setFormState] = useState<ShopifyLoginPayload>({
    emailAddress: "",
    password: "",
    rememberMe: false,
    reCaptchaToken: "",
  });

  const [errors, setErrors] = useState<{
    emailAddress?: string;
    password?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Social registration form ---
  const [view, setView] = useState<"login" | "social_register">("login");
  const [socialPending, setSocialPending] = useState<{
    token: string;
    provider: string;
  } | null>(null);
  const [registerForm, setRegisterForm] = useState<SocialRegisterForm>({
    companyName: "",
    shopifySiteUrl: "",
  });
  const [registerErrors, setRegisterErrors] = useState<SocialRegisterErrors>(
    {},
  );
  const [isRegistering, setIsRegistering] = useState(false);

  const handleGoogleLogin = async () => {
    localStorage.setItem("oauth_provider", "google");
    localStorage.setItem("oauth_target", "shopify");
    const data = await getGoogleAuthUrl("marketplace-admin-shopify");
    if (data?.url) {
      window.location.href = data.url;
    }
  };

  const handleMicrosoftLogin = async () => {
    localStorage.setItem("oauth_provider", "microsoft");
    localStorage.setItem("oauth_target", "shopify");
    const data = await getMicrosoftAuthUrl("marketplace-admin-shopify");
    if (data?.url) {
      window.location.href = data.url;
    }
  };

  const handleAppleLogin = async () => {
    localStorage.setItem("oauth_provider", "apple");
    localStorage.setItem("oauth_target", "shopify");
    const data = await getAppleAuthUrl("marketplace-admin-shopify");
    if (data?.url) {
      window.location.href = data.url;
    }
  };

  // Handle OAuth callback
  // id_token may come directly as a hash fragment, or be relayed here from /login
  React.useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const queryParams = new URLSearchParams(window.location.search);
    const idToken = hashParams.get("id_token") ?? queryParams.get("id_token");

    if (idToken) {
      // provider may be stored in localStorage, or relayed in hash params
      const provider =
        hashParams.get("provider") ??
        queryParams.get("provider") ??
        localStorage.getItem("oauth_provider") ??
        "google";

      localStorage.removeItem("oauth_provider");
      localStorage.removeItem("oauth_target");
      window.history.replaceState(null, null, window.location.pathname);

      socialLogin(provider, idToken, 'marketplace-admin-shopify').then((result) => {
        if (result === "needs_register") {
          setSocialPending({ token: idToken, provider });
          setView("social_register");
        }
      });
    }
  }, [socialLogin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate(formState, shopifyLoginFormConstraints);
    if (validationErrors) {
      const formattedErrors: { emailAddress?: string; password?: string } = {};
      if (validationErrors.emailAddress) {
        formattedErrors.emailAddress = validationErrors.emailAddress[0];
      }
      if (validationErrors.password) {
        formattedErrors.password = validationErrors.password[0];
      }
      setErrors(formattedErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const authPayload = await api.shopifyApi.login(formState);
      if (authPayload) {
        User.authenticate(authPayload);
        history.push("/stores/add");
      }
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validate(
      registerForm,
      socialRegisterFormConstraints,
    );
    if (validationErrors) {
      setRegisterErrors({
        companyName: validationErrors.companyName?.[0],
        shopifySiteUrl: validationErrors.shopifySiteUrl?.[0],
      });
      return;
    }

    setRegisterErrors({});
    setIsRegistering(true);

    try {
      const result = await socialRegister({
        token: socialPending.token,
        provider: socialPending.provider,
        companyName: registerForm.companyName,
        shopifySiteUrl: registerForm.shopifySiteUrl,
      });

      if (result) {
        await socialLogin(socialPending.provider, socialPending.token);
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleBackToLogin = () => {
    setView("login");
    setSocialPending(null);
    setRegisterForm({ companyName: "", shopifySiteUrl: "" });
    setRegisterErrors({});
  };

  const onCaptchaChange = (token: string | null) => {
    setFormState({
      ...formState,
      reCaptchaToken: token || "",
    });
  };

  const handleEmailChange = (value: string) => {
    setFormState({ ...formState, emailAddress: value });
    if (errors.emailAddress) {
      setErrors({ ...errors, emailAddress: undefined });
    }
  };

  const handlePasswordChange = (value: string) => {
    setFormState({ ...formState, password: value });
    if (errors.password) {
      setErrors({ ...errors, password: undefined });
    }
  };

  const socialLoginButtons = [
    {
      icon: <GoogleIcon />,
      label: "Google",
      onClick: handleGoogleLogin,
    },
    {
      icon: <MicrosoftIcon />,
      label: "Microsoft",
      onClick: handleMicrosoftLogin,
    },
    {
      icon: <AppleIcon />,
      label: "Apple",
      onClick: handleAppleLogin,
    },
  ];

  if (view === "social_register") {
    return (
      <div className={styles.loginPage}>
        <div className={styles.loginContainer}>
          <form className={styles.loginForm} onSubmit={handleRegisterSubmit}>
            <div className={styles.loginCard}>
              <div className={styles.logoContainer}>
                <Logo />
              </div>
              <div className={styles.title}>Complete Registration</div>
              <div className={styles.formFields}>
                <div className={styles.inputGroup}>
                  <label htmlFor="company-name" className={styles.label}>
                    Company Name
                  </label>
                  <input
                    id="company-name"
                    type="text"
                    required
                    value={registerForm.companyName}
                    onChange={(e) => {
                      setRegisterForm({
                        ...registerForm,
                        companyName: e.target.value,
                      });
                      if (registerErrors.companyName) {
                        setRegisterErrors({
                          ...registerErrors,
                          companyName: undefined,
                        });
                      }
                    }}
                    className={
                      registerErrors.companyName
                        ? styles.inputError
                        : styles.input
                    }
                    placeholder="My Company Ltd"
                  />
                  {registerErrors.companyName && (
                    <p className={styles.errorMessage}>
                      {registerErrors.companyName}
                    </p>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="shopify-url" className={styles.label}>
                    Shopify Store URL
                  </label>
                  <input
                    id="shopify-url"
                    type="url"
                    required
                    value={registerForm.shopifySiteUrl}
                    onChange={(e) => {
                      setRegisterForm({
                        ...registerForm,
                        shopifySiteUrl: e.target.value,
                      });
                      if (registerErrors.shopifySiteUrl) {
                        setRegisterErrors({
                          ...registerErrors,
                          shopifySiteUrl: undefined,
                        });
                      }
                    }}
                    className={
                      registerErrors.shopifySiteUrl
                        ? styles.inputError
                        : styles.input
                    }
                    placeholder="https://mystore.myshopify.com"
                  />
                  {registerErrors.shopifySiteUrl && (
                    <p className={styles.errorMessage}>
                      {registerErrors.shopifySiteUrl}
                    </p>
                  )}
                </div>
              </div>

              <div style={{ height: "1rem" }} />

              <button
                type="submit"
                disabled={isRegistering}
                className={styles.submitButton}
              >
                {isRegistering ? "Registering..." : "Complete Registration"}
              </button>

              <div className={styles.signupLink}>
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className={styles.signupLinkText}
                >
                  &larr; Back to login
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginContainer}>
        <form className={styles.loginForm} onSubmit={handleSubmit}>
          <div className={styles.loginCard}>
            <div className={styles.logoContainer}>
              <Logo />
            </div>
            <div className={styles.title}>Shopify Admin Login</div>
            <div className={styles.formFields}>
              <div className={styles.inputGroup}>
                <label htmlFor="email-address" className={styles.label}>
                  Email address
                </label>
                <input
                  id="email-address"
                  type="email"
                  autoComplete="email"
                  required
                  value={formState.emailAddress}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  className={
                    errors.emailAddress ? styles.inputError : styles.input
                  }
                  placeholder="Enter your email"
                />
              </div>
              <div className={styles.inputGroup}>
                <label htmlFor="password" className={styles.label}>
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formState.password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  className={errors.password ? styles.inputError : styles.input}
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={formState.rememberMe}
                onChange={(e) =>
                  setFormState({ ...formState, rememberMe: e.target.checked })
                }
              />
              <label htmlFor="rememberMe" className={styles.checkboxLabel}>
                Remember me
              </label>
            </div>

            <ReCAPTCHA
              sitekey={RE_CAPTCHA_SITE_KEY}
              onChange={onCaptchaChange}
              className={styles.recaptchaContainer}
            />

            <div className={styles.signupLink}>
              <span>Or</span>{" "}
              <button
                type="button"
                onClick={() => history.push("/shopify/signup")}
                className={styles.signupLinkText}
              >
                create an account
              </button>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={styles.submitButton}
            >
              {isSubmitting ? "Signing in..." : "Sign in"}
            </button>

            <div className={styles.dividerContainer}>
              <div className={styles.dividerLine} />
              <span className={styles.dividerText}>Or continue with</span>
              <div className={styles.dividerLine} />
            </div>

            <div className={styles.socialButtonsContainer}>
              {socialLoginButtons.map((button) => (
                <button
                  type="button"
                  className={styles.socialButton}
                  onClick={button.onClick}
                >
                  {button.icon}
                  Login with {button.label}
                </button>
              ))}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export { ShopifyLoginPage };
